import React, { useState } from 'react'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'

interface SortableHeaderProps<T extends string> {
  field: T
  currentField: T
  direction: 'asc' | 'desc'
  onSort: (field: T) => void
  children: React.ReactNode
  className?: string
}

export function SortableHeader<T extends string>({
  field,
  currentField,
  direction,
  onSort,
  children,
  className = '',
}: SortableHeaderProps<T>) {
  const isActive = currentField === field

  return (
    <th
      className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <div className="flex flex-col -space-y-1">
          <ChevronUpIcon
            className={`h-3 w-3 ${isActive && direction === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
          />
          <ChevronDownIcon
            className={`h-3 w-3 ${isActive && direction === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
          />
        </div>
      </div>
    </th>
  )
}

// Hook for managing sort state
export function useSort<T extends string>(defaultField: T, defaultDirection: 'asc' | 'desc' = 'asc') {
  const [sortField, setSortField] = useState<T>(defaultField)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultDirection)

  const handleSort = (field: T) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  return {
    sortField,
    sortDirection,
    handleSort,
  }
}
